/* eslint-disable no-empty-pattern */
import { createSelector, createSlice } from '@reduxjs/toolkit';
import * as R from 'ramda';

import { CancelToken } from 'axios';

// Time Helpers
import { format, subDays } from 'date-fns';

// Helpers
import http from 'utils/http';
import { getStringifiedQueryParams } from 'utils/helpers/fetch';
import { formatTimeToAmPm, formatTimeToCst } from 'feature/loading-management/utils/timeHelpers';

const initialState = {
  activeLocation: {},
  activeState: 'All',
  activeTab: 'waitTimes',
  bolDate: format(subDays(new Date(), 1), 'yyyy-MM-dd'),
  bolHistory: [],
  bolHistoryPending: false,
  carrierSortColumn: 'performanceScore',
  carrierSortDirection: 'dsc',
  filtersOpen: false,
  lastFetchTime: '',
  lastLocationDetailsFetchTime: '',
  locationDetails: {},
  locationDetailsPending: true,
  locationLoadTimes: {},
  locationLoadTimesPending: true,
  locations: [],
  locationsPending: true,
  mobileDrawerOpen: false,
  mobileFilterDrawerOpen: false,
  searchField: '',
  selectedAffiliate: 'all',
  sortColumn: 'name',
  sortDirection: 'dsc',
  states: [],
};

const reducers = {
  resetWaitTimes: R.always(initialState),
  setActiveLocation: R.assoc('activeLocation'),
  setActiveState: R.assoc('activeState'),
  setActiveTab: R.assoc('activeTab'),
  setBolDate: R.assoc('bolDate'),
  setBolHistory: R.assoc('bolHistory'),
  setBolHistoryPending: R.assoc('bolHistoryPending'),
  setCarrierSortColumn: R.assoc('carrierSortColumn'),
  setCarrierSortDirection: R.assoc('carrierSortDirection'),
  setFiltersOpen: R.assoc('filtersOpen'),
  setLastFetchTime: R.assoc('lastFetchTime'),
  setLastLocationDetailsFetchTime: R.assoc('lastLocationDetailsFetchTime'),
  setLocationDetails: R.assoc('locationDetails'),
  setLocationDetailsPending: R.assoc('locationDetailsPending'),
  setLocationLoadTimes: R.assoc('locationLoadTimes'),
  setLocationLoadTimesPending: R.assoc('locationLoadTimesPending'),
  setLocations: R.assoc('locations'),
  setLocationsPending: R.assoc('locationsPending'),
  setMobileDrawerOpen: R.assoc('mobileDrawerOpen'),
  setMobileFilterDrawerOpen: R.assoc('mobileFilterDrawerOpen'),
  setSearchField: R.assoc('searchField'),
  setSelectedAffiliate: R.assoc('selectedAffiliate'),
  setSortColumn: R.assoc('sortColumn'),
  setSortDirection: R.assoc('sortDirection'),
  setStates: R.assoc('states'),
};

const { actions, reducer } = createSlice({
  initialState,
  name: 'rackWaitTimes',
  // eslint-disable-next-line react-hooks/rules-of-hooks
  reducers: R.map(singleReducer => R.flip(R.useWith(singleReducer, [R.prop('payload')])), reducers),
});

export const {
  resetWaitTimes,
  setActiveLocation,
  setActiveState,
  setActiveTab,
  setBolDate,
  setBolHistory,
  setBolHistoryPending,
  setCarrierSortColumn,
  setCarrierSortDirection,
  setFiltersOpen,
  setLastFetchTime,
  setLastLocationDetailsFetchTime,
  setLocationDetails,
  setLocationDetailsPending,
  setLocationLoadTimes,
  setLocationLoadTimesPending,
  setLocations,
  setLocationsPending,
  setMobileDrawerOpen,
  setMobileFilterDrawerOpen,
  setSelectedAffiliate,
  setSortColumn,
  setSortDirection,
  updateFilters,
  setSearchField,
  setStates,
} = actions;

// Selectors
export const getItem = item => R.path(['loadingManagement', 'rackWaitTimes', item]);

const getFilteredStates = createSelector(
  getItem(['activeState']),
  getItem(['locations']),
  (activeState, dataList) => {
    if (activeState !== 'All') {
      const filter = dataList.filter(
        // TODO: remove null check when full data is returned from both APIs
        ({ address }) => address?.state.toLowerCase() === activeState.toLowerCase()
      );
      return filter;
    }
    return dataList;
  }
);

// This is to hide any locations in the list that don't have the nameShort property
// this is so we don't have blank items in the list
const filterNameShort = createSelector(getFilteredStates, filtered =>
  filtered.filter(({ nameShort }) => nameShort)
);

const getFilteredLocations = createSelector(
  filterNameShort,
  getItem(['searchField']),
  (filteredStates, searchField) => {
    if (searchField) {
      // TODO: remove name when data for left side is not mismatched
      return filteredStates.filter(({ name, nameShort }) => {
        const val = nameShort ?? name;
        return val.toLowerCase().includes(searchField.toLowerCase());
      });
    }
    return filteredStates;
  }
);

// get one item and next item
// get their name property and convert to lower case
// if direction is asc then sort by ascending order
// else if direction is dsc then sort by descending order
// if same then dont change their location in the array
const sortLocationsByName = (arr, col, direction) =>
  arr.sort((a, b) => {
    const aName = (a[col] || '').toLowerCase();
    const bName = (b[col] || '').toLowerCase();
    if (direction === 'dsc' ? aName < bName : aName > bName) return -1;
    if (direction === 'dsc' ? aName > bName : aName < bName) return 1;
    return 0;
  });

// get one item and next item
// get their time property from their waitTimes array
// if direction is asc then sort by ascending order
// else if direction is dsc then sort by descending order
// if same then dont change their location in the array
const sortLocationsByTime = (arr, col, direction) =>
  arr.sort((a, b) => {
    const aTime = a.waitTimes.find(({ key }) => key === col) || {};
    const bTime = b.waitTimes.find(({ key }) => key === col) || {};
    if (direction === 'dsc' ? aTime.minutes < bTime.minutes : aTime.minutes > bTime.minutes)
      return -1;
    if (direction === 'dsc' ? aTime.minutes > bTime.minutes : aTime.minutes < bTime.minutes)
      return 1;
    return 0;
  });

const getSortedLocations = (locations, column, direction) => {
  // Create a copy without the copy it wont sort
  const copy = [...locations];
  let sorted = [];
  if (column === 'name') {
    sorted = sortLocationsByName(copy, column, direction);
  } else {
    sorted = sortLocationsByTime(copy, column, direction);
  }
  return sorted;
};

export const getLocations = createSelector(
  getFilteredLocations,
  getItem(['sortColumn']),
  getItem(['sortDirection']),
  (filtered, column, direction) => getSortedLocations(filtered, column, direction)
);

const sortLocationsByNumber = (arr, col, dir) =>
  arr.sort((a, b) => {
    const aName = a[col];
    const bName = b[col];
    if (dir === 'dsc' ? aName > bName : aName < bName) return -1;
    if (dir === 'dsc' ? aName < bName : aName > bName) return 1;
    return 0;
  });

const getSortedCarriers = (loadTimes, col, dir) => {
  // Create a copy without the copy it wont sort
  const copy = loadTimes.carriers ? [...loadTimes.carriers] : [];
  let sorted = [];
  if (col === 'carrierName') {
    sorted = sortLocationsByName(copy, col, dir);
  } else {
    sorted = sortLocationsByNumber(copy, col, dir);
  }
  return sorted;
};

export const getCarrierPerformance = createSelector(
  getItem(['locationLoadTimes']),
  getItem(['carrierSortColumn']),
  getItem(['carrierSortDirection']),
  (loadTimes, column, direction) => getSortedCarriers(loadTimes, column, direction)
);

// Actions
const baseURL = 'rack-wait-times';

let terminalToken;
export const fetchActiveTerminals = () => dispatch => {
  dispatch(setLocationsPending(true));

  if (terminalToken !== undefined) {
    terminalToken.cancel('terminalToken operation canceled due to new request.');
  }

  // Save the cancel token for the current request
  terminalToken = CancelToken.source();

  const url = `${baseURL}/get-active-terminal-locations`;
  return http
    .get(url, { cancelToken: terminalToken.token })
    .then(({ data }) => ({ data, error: '', successful: true }))
    .catch(error => ({ error, successful: true }));
};

let bolHistoryToken;
export const fetchBolHistory = queryParams => dispatch => {
  if (bolHistoryToken !== undefined)
    bolHistoryToken.cancel('locationDetails operation canceled due to new request.');

  bolHistoryToken = CancelToken.source();
  const queryString = getStringifiedQueryParams(queryParams);
  const url = `${baseURL}/get-bol-details?${queryString}`;
  http
    .get(url, { cancelToken: bolHistoryToken.token })
    .then(({ data }) => {
      dispatch(setBolHistory(data));
    })
    .catch(error => error)
    .finally(() => dispatch(setBolHistoryPending(false)));
};

let waitTimesToken;
export const fetchWaitTimes = () => dispatch => {
  if (waitTimesToken !== undefined) {
    waitTimesToken.cancel('waitTimesToken operation canceled due to new request.');
  }

  // Save the cancel token for the current request
  waitTimesToken = CancelToken.source();

  const url = `${baseURL}/get-wait-times`;
  return http
    .get(url, { cancelToken: waitTimesToken.token })
    .then(({ data }) => ({ data, error: '', successful: true }))
    .catch(error => ({ error, successful: true }))
    .finally(() => {
      dispatch(setLocationsPending(false));
      dispatch(setLastFetchTime(formatTimeToAmPm(formatTimeToCst(new Date()))));
    });
};

let locationDetailsToken;
export const fetchLocationDetails = queryParams => dispatch => {
  dispatch(setLocationDetailsPending(true));
  dispatch(setLocationDetails({}));

  if (locationDetailsToken !== undefined)
    locationDetailsToken.cancel('locationDetails operation canceled due to new request.');

  locationDetailsToken = CancelToken.source();
  const queryString = getStringifiedQueryParams(queryParams);
  const url = `${baseURL}/get-wait-time-location-details?${queryString}`;
  http
    .get(url, { cancelToken: locationDetailsToken.token })
    .then(({ data }) => {
      dispatch(setLocationDetails(data));
    })
    .catch(error => ({ error, successful: true }))
    .finally(() => {
      dispatch(setLastLocationDetailsFetchTime(formatTimeToAmPm(formatTimeToCst(new Date()))));
      dispatch(setLocationDetailsPending(false));
    });
};

let locationLoadTimeToken;
export const fetchLocationLoadTimes = queryParams => dispatch => {
  dispatch(setLocationLoadTimesPending(true));
  setLocationLoadTimes({});

  if (locationLoadTimeToken !== undefined) {
    // Prevents crashing when switching selectors while data is still loading
    setLocationLoadTimes({});
    locationLoadTimeToken.cancel('locationDetails operation canceled due to new request.');
  }

  locationLoadTimeToken = CancelToken.source();
  const queryString = getStringifiedQueryParams(queryParams);
  const url = `${baseURL}/get-location-load-times-details?${queryString}`;
  http
    .get(url, { cancelToken: locationLoadTimeToken.token })
    .then(({ data }) => {
      dispatch(setLocationLoadTimes(data));
    })
    .catch(error => ({ error, successful: true }))
    .finally(() => dispatch(setLocationLoadTimesPending(false)));
};

export default reducer;
